/**
 * RPE preset segment placement over a motion column (W2-04a; DESIGN.md §5.6.1, §8.3; rpe-editor-features.md
 * E§3.8 EVT-01..05, E§4.4):
 *
 *   R ... R   first R = start key at the mouse beat, second R = end key: one segment;
 *   Q ... R   Q instead of the first R = a "Stay, then jump" segment (RPE lock, EVT-02);
 *   R ... 4   a digit instead of the second R = that RPE easing id (EVT-03); two digits within 400 ms = a two-digit
 *             id (1 then 5 = 15; ids above 29 keep the first digit; a lone 0 = Linear);
 *   Esc       cancels (the shared placement state `{ kind: 'segment' }` of the editor store).
 *
 * The column is the one under the mouse (which becomes the focused column), else the focused column at the mouse
 * beat. Placement follows RPE's inheritance (EVT-05, E§4.4): the new segment starts at the curve's current value
 * (a shape-preserving split), ends at the same value (end = start) and uses the last used curve (initially Linear),
 * or Stay / the digit's easing. Keys strictly inside the span refuse the placement (EVT-04: the ghost turns red).
 * When the end lands inside an existing segment, its key arrives at the start value and leaves on the old curve (a
 * jump), so the motion after the new segment does not change. With Position X and Y linked (RPE "XY bind") the
 * other axis gets the same segment: its own value at the start, end = start, the same curve.
 */

import { Core } from '../../core/commands/index';
import { HOLD } from '../../core/ease/index';
import type { Ease } from '../../core/ease/types';
import type { KeyId } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import { snapBeat } from '../../render/timeline/axis';
import type { ColumnRef } from '../../state/types';
import { findLane, keysInsideSpan, xyPartnerRef, type ColumnSpec } from './columnSpec';
import { laneRef } from './columnScene';
import { confirmGuidedEdit, insertOnRef, keyAt, tailOf } from './motionActions';
import { m } from './motion.messages';
import type { MotionSession } from './session';
import { ref, runTx, snapDiv, type Services } from './txn';

/** Second digit window (DESIGN.md §5.6.1). */
export const DIGIT_WINDOW_MS = 400;

interface DigitState {
  readonly first: number;
  readonly timer: ReturnType<typeof setTimeout>;
}
const digitStates = new WeakMap<MotionSession, DigitState>();

/** Where an R / Q press points: the motion column under the mouse, else the focused column at the mouse beat. */
export function placementTarget(
  session: MotionSession,
): { readonly spec: ColumnSpec; readonly target: ColumnRef; readonly beat: number } | null {
  const s = session.services;
  const hover = session.hover;
  if (hover !== null) {
    const spec = session.specs.find((c) => c.id === hover.instanceId);
    const lane = spec?.lanes[Math.max(0, hover.lane)];
    if (spec !== undefined && lane !== undefined) return { spec, target: laneRef(spec, lane), beat: hover.beat };
  }
  const focus = s.editor.getState().focusedColumn;
  if (focus === null) return null;
  const found = findLane(session.specs, focus);
  if (found === null) return null;
  const beat = pointerBeat(session);
  if (beat === null) return null;
  return { spec: found.column, target: focus, beat };
}

/** The beat under the mouse (see MotionSession.pointerBeat). */
export function pointerBeat(session: MotionSession): number | null {
  return session.pointerBeat();
}

function beatOf(s: Services, f: number): Beat {
  return snapBeat(Math.max(0, f), snapDiv(s));
}

/** First press (R, or Q for a Stay segment): starts the placement. */
export async function startPlacement(session: MotionSession, constant: boolean): Promise<boolean> {
  const s = session.services;
  const store = s.store;
  if (store === null) return false;
  const tgt = placementTarget(session);
  if (tgt === null) {
    s.ui.toast(ref(m.placeNoColumn), { kind: 'warning' });
    return false;
  }
  if (!(await confirmGuidedEdit(s, session, tgt.spec))) return false;
  const st = s.editor.getState();
  st.setFocusedColumn(tgt.target);
  st.setPlacement({
    kind: 'segment',
    line: tgt.target.line,
    prop: tgt.target.prop,
    layer: tgt.target.layer,
    start: beatOf(s, tgt.beat),
    constant,
    digits: '',
  });
  s.ui.setGestureHints([
    { chord: 'KeyR', label: ref(m.placeHintEnd) },
    { chord: 'Digit1', label: ref(m.placeHintDigit) },
    { chord: 'Escape', label: ref(m.placeHintCancel) },
  ]);
  session.invalidate('overlay');
  return true;
}

export function cancelPlacement(session: MotionSession): void {
  const s = session.services;
  const d = digitStates.get(session);
  if (d !== undefined) {
    clearTimeout(d.timer);
    digitStates.delete(session);
  }
  if (s.editor.getState().placement?.kind === 'segment') s.editor.getState().setPlacement(null);
  s.ui.setGestureHints(null);
  session.invalidate('overlay');
}

/** The end beat of a pending placement: the mouse beat over a motion column (snapped), else the start. */
function endBeat(session: MotionSession): Beat | null {
  const s = session.services;
  const p = s.editor.getState().placement;
  if (p?.kind !== 'segment') return null;
  const f = pointerBeat(session);
  return f === null ? null : snapBeat(Math.max(0, f), snapDiv(s));
}

/**
 * Second press: places the segment [start, end] with `ease` (null = Stay for Q placements, else the last used curve).
 * Returns the new segment's start key, or null when refused (the placement stays pending so the mouse can move).
 */
export function finishPlacement(session: MotionSession, ease: Ease | null): KeyId | null {
  const s = session.services;
  const store = s.store;
  const p = s.editor.getState().placement;
  if (store === null || p?.kind !== 'segment') return null;
  const end = endBeat(session);
  if (end === null || !Beats.lt(p.start, end)) {
    s.ui.toast(ref(m.placeTooShort), { kind: 'warning' });
    return null;
  }
  const target: ColumnRef = { line: p.line, prop: p.prop, layer: p.layer };
  if (keysInsideSpan(store.doc, store.idx, target, Beats.toF64(p.start), Beats.toF64(end))) {
    s.ui.toast(ref(m.placeBlocked), { kind: 'warning' });
    return null;
  }
  const curve: Ease = p.constant ? HOLD : (ease ?? session.lastCurve);
  const tail = tailOf(s, session);
  const out = runTx(
    s,
    { label: ref(m.txPlaceSegment), command: 'motion.placeSegment', focus: { beat: p.start, line: p.line } },
    (tx) => {
      const k0 = insertOnRef(tx, target, p.start, tail).key;
      const k1 = insertOnRef(tx, target, end, tail).key;
      endAtStart(tx, k0, k1);
      // XY bind: the other axis (keyed at both beats by the core) also ends where it starts.
      const partner = xyPartnerRef(tx.doc, target);
      const pt = partner === null ? undefined : tx.idx.track(partner.line, partner.prop, partner.layer);
      if (pt !== undefined) {
        const p0 = keyAt(tx, pt, p.start);
        const p1 = keyAt(tx, pt, end);
        if (p0 !== undefined && p1 !== undefined && tx.idx.nextKey(p0) === p1) endAtStart(tx, p0, p1);
      }
      // One curve for the segment (Core.keys.setEase re-eases the linked axis' segment too).
      Core.keys.setEase(tx, { segments: [k0], ease: curve });
      tx.setSelection({ segments: [k0], keys: [], notes: [] });
      return k0;
    },
  );
  cancelPlacement(session);
  if (!out.ok) return null;
  if (ease !== null && !p.constant) session.lastCurve = ease;
  return out.value;
}

/**
 * End = start (EVT-05): the end key arrives at the start key's value. A key that continues into an existing segment
 * keeps leaving on its old value (a jump), so the motion after the new segment does not change.
 */
function endAtStart(tx: TxBuilder, k0: KeyId, k1: KeyId): void {
  const startValue = tx.doc.keys.get(k0)?.vOut;
  const endRec = tx.doc.keys.get(k1);
  if (startValue === undefined || endRec === undefined || sameValue(endRec.vIn, startValue)) return;
  const keepOut = endRec.vOut;
  Core.keys.setKeyValues(tx, { keys: [k1], part: 'in', value: startValue });
  if (tx.idx.nextKey(k1) === undefined) Core.keys.setKeyValues(tx, { keys: [k1], part: 'out', value: startValue });
  else if (!sameValue(tx.doc.keys.get(k1)?.vOut, keepOut))
    Core.keys.setKeyValues(tx, { keys: [k1], part: 'out', value: keepOut });
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => x === b[i]);
  return a === b;
}

/** An RPE easing id as a whole-curve tween (ids outside 1..29 become Linear). */
export function easeOfId(id: number): Ease {
  const n = Number.isInteger(id) && id >= 1 && id <= 29 ? id : 1;
  return { kind: 'tween', id: n, left: 0, right: 1 };
}

/**
 * A digit during a placement. 3..9 place at once; 1 and 2 wait up to 400 ms for a second digit (10..29); 0 waits
 * for a second digit (05 = 5) and alone means Linear. Returns true when the digit was used.
 */
export function placementDigit(session: MotionSession, digit: number): boolean {
  const s = session.services;
  const p = s.editor.getState().placement;
  if (p?.kind !== 'segment' || !Number.isInteger(digit) || digit < 0 || digit > 9) return false;
  const pending = digitStates.get(session);
  if (pending !== undefined) {
    clearTimeout(pending.timer);
    digitStates.delete(session);
    const two = pending.first * 10 + digit;
    finishPlacement(session, easeOfId(two >= 1 && two <= 29 ? two : pending.first === 0 ? digit : pending.first));
    return true;
  }
  if (digit >= 3) {
    finishPlacement(session, easeOfId(digit));
    return true;
  }
  const timer = setTimeout(() => {
    digitStates.delete(session);
    finishPlacement(session, easeOfId(digit === 0 ? 1 : digit));
  }, DIGIT_WINDOW_MS);
  digitStates.set(session, { first: digit, timer });
  s.editor.getState().setPlacement({ ...p, digits: String(digit) });
  return true;
}

/** R (or Q) pressed: start a placement, or end the pending one with the last used curve. */
export async function placementPress(session: MotionSession, constant: boolean): Promise<void> {
  const p = session.services.editor.getState().placement;
  if (p?.kind === 'segment') {
    if (digitStates.has(session)) return;
    finishPlacement(session, null);
    return;
  }
  await startPlacement(session, constant);
}

/** Column id of a placement (for redraws). */
export function placementColumnId(session: MotionSession): string | null {
  const p = session.services.editor.getState().placement;
  if (p?.kind !== 'segment') return null;
  const found = findLane(session.specs, { line: p.line, prop: p.prop, layer: p.layer });
  return found?.column.id ?? null;
}
